import { useEffect, useRef, useState } from "react";
import { Camera, PackagePlus, ScanLine, Search, StopCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, getApiError } from "@/lib/api";
import { createCameraBarcodeSession, supportsCameraBarcodeScan } from "@/lib/barcodeScannerBridge";
import { toast } from "@/components/ui/sonner";


const defaultConfirmForm = {
  quantity: "",
  unit: "g",
  purchase_unit_label: "",
  location: "pantry",
  status: "sealed",
  expiry_date: "",
};


export const PantryBarcodeImportCard = ({ onImported }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [barcode, setBarcode] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [resolved, setResolved] = useState(null);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [confirmForm, setConfirmForm] = useState(defaultConfirmForm);
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selectorOpen, setSelectorOpen] = useState(false);

  const cameraSupported = supportsCameraBarcodeScan();

  const stopScanner = () => {
    if (streamRef.current) {
      streamRef.current.stop();
      streamRef.current = null;
    }
    setScanning(false);
  };

  useEffect(() => () => stopScanner(), []);

  const selectedMatch = resolved?.matches?.find((match) => match.variant_id === selectedVariantId) || resolved?.matches?.[0] || null;

  useEffect(() => {
    if (!selectedMatch) {
      return;
    }

    setConfirmForm((current) => ({
      ...current,
      quantity: String(selectedMatch.pack_size || current.quantity || ""),
      unit: selectedMatch.unit || current.unit,
      purchase_unit_label: selectedMatch.purchase_unit_label || current.purchase_unit_label,
      location: selectedMatch.default_location || current.location,
      status: selectedMatch.default_status || current.status,
    }));
  }, [selectedMatch]);

  const applyResolvedPayload = (payload, nextBarcode = barcode) => {
    const firstMatch = payload.variant_id
      ? payload.matches?.find((match) => match.variant_id === payload.variant_id) || payload.matches?.[0]
      : payload.matches?.[0];
    setResolved(payload);
    setSelectedVariantId(payload.variant_id || payload.matches?.[0]?.variant_id || null);
    if (firstMatch) {
      setConfirmForm((current) => ({
        ...current,
        quantity: String(firstMatch.pack_size || current.quantity || ""),
        unit: firstMatch.unit || current.unit,
        purchase_unit_label: firstMatch.purchase_unit_label || current.purchase_unit_label,
        location: firstMatch.default_location || current.location,
        status: firstMatch.default_status || current.status,
      }));
    }
    if (nextBarcode) {
      setBarcode(nextBarcode);
    }
  };

  const resolveBarcode = async (value) => {
    try {
      const response = await api.get("/ingredients/resolve-barcode", {
        params: { barcode: value },
      });
      applyResolvedPayload(response.data, value);
      setSearchQuery("");
    } catch (error) {
      setResolved(null);
      toast.error(getApiError(error));
    }
  };

  const searchByProductName = async () => {
    try {
      const response = await api.get("/pantry/search-products", {
        params: { query: searchQuery },
      });
      applyResolvedPayload(response.data, "");
      toast.success("Productmatches gevonden.");
    } catch (error) {
      setResolved(null);
      toast.error(getApiError(error));
    }
  };

  const startScanner = async () => {
    if (!cameraSupported) {
      toast.error("Camera-scan wordt hier niet ondersteund. Gebruik de handmatige barcode fallback.");
      return;
    }

    try {
      streamRef.current = await createCameraBarcodeSession({
        videoElement: videoRef.current,
        onDetect: async (firstCode) => {
          setScanning(false);
          await resolveBarcode(firstCode);
          toast.success("Barcode gescand.");
        },
      });
      setScanning(true);
    } catch (error) {
      if (error?.name === "NotSupportedError") {
        toast.error("Deze camera ondersteunt geen barcode-herkenning. Gebruik de handmatige fallback.");
        stopScanner();
        return;
      }
      toast.error("Camera kon niet worden gestart. Gebruik de handmatige barcode fallback.");
      stopScanner();
    }
  };

  const confirmInventoryItem = async () => {
    if (!selectedMatch) {
      return;
    }

    try {
      setBusy(true);
      const response = await api.post("/pantry/confirm-item", {
        ingredient_id: selectedMatch.ingredient_id,
        variant_id: selectedMatch.variant_id,
        quantity: Number(confirmForm.quantity),
        unit: confirmForm.unit,
        purchase_unit_label: confirmForm.purchase_unit_label,
        location: confirmForm.location,
        status: confirmForm.status,
        expiry_date: confirmForm.expiry_date || null,
      });
      onImported(response.data);
      setConfirmForm(defaultConfirmForm);
      setResolved(null);
      setBarcode("");
      setSearchQuery("");
      toast.success("Inventory item toegevoegd aan je voorraad.");
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="rounded-[28px] border border-stone-200 bg-white/90 p-4" data-testid="pantry-barcode-import-card">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" data-testid="pantry-barcode-import-kicker">
          Camera-first inventory
        </p>
        <h3 className="mt-2 text-lg font-semibold text-stone-900" data-testid="pantry-barcode-import-title">
          Scan eerst, of val terug op barcode/plakveld en productnaam zoeken.
        </h3>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button className="rounded-full bg-stone-900 text-white hover:bg-stone-800" onClick={startScanner} disabled={scanning} data-testid="pantry-barcode-camera-button">
            <Camera className="mr-2 h-4 w-4" />
            Start camera scan
          </Button>
          {scanning ? (
            <Button variant="outline" className="rounded-full border-stone-200 bg-white" onClick={stopScanner} data-testid="pantry-barcode-stop-button">
              <StopCircle className="mr-2 h-4 w-4" />
              Stop
            </Button>
          ) : null}
        </div>

        {scanning ? (
          <div className="mt-4 overflow-hidden rounded-[24px] border border-stone-200 bg-stone-950" data-testid="pantry-barcode-video-shell">
            <video ref={videoRef} className="aspect-[4/3] w-full object-cover" muted playsInline data-testid="pantry-barcode-video" />
          </div>
        ) : null}

        <div className="mt-4 grid grid-cols-[1fr_auto] gap-2">
          <Input value={barcode} onChange={(event) => setBarcode(event.target.value)} placeholder="Plak handmatig een barcode" className="h-12 rounded-2xl border-stone-200" data-testid="pantry-barcode-input" />
          <Button variant="outline" className="h-12 rounded-full border-stone-200 bg-white" onClick={() => resolveBarcode(barcode)} data-testid="pantry-barcode-resolve-button">
            <ScanLine className="mr-2 h-4 w-4" />
            Zoek
          </Button>
        </div>

        <div className="mt-3 grid grid-cols-[1fr_auto] gap-2">
          <Input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Zoek op product of ingrediënt" className="h-12 rounded-2xl border-stone-200" data-testid="pantry-product-search-input" />
          <Button variant="outline" className="h-12 rounded-full border-stone-200 bg-white" onClick={searchByProductName} data-testid="pantry-product-search-button">
            <Search className="mr-2 h-4 w-4" />
            Zoek naam
          </Button>
        </div>

        {resolved && selectedMatch ? (
          <div className="mt-4 rounded-[22px] border border-dashed border-emerald-200 bg-emerald-50 p-4" data-testid="pantry-barcode-result-card">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-stone-700" data-testid="pantry-result-source-pill">
                {resolved.search_source === "product_search" || resolved.search_source === "product_search_inferred" ? "Naamzoeker" : resolved.search_source === "exact_barcode" ? "Exacte barcode-match" : "Generieke barcode fallback"}
              </span>
              <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-stone-700" data-testid="pantry-result-category-pill">
                {selectedMatch.category}
              </span>
              <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-stone-700" data-testid="pantry-result-confidence-pill">
                {selectedMatch.confidence_label}
              </span>
              {resolved.auto_selected ? <span className="rounded-full bg-emerald-700 px-3 py-1 text-xs font-medium text-white" data-testid="pantry-result-auto-pill">Klaar om toe te voegen</span> : null}
            </div>
            <p className="mt-3 text-sm font-semibold text-stone-900" data-testid="pantry-barcode-result-title">{selectedMatch.ingredient_name}</p>
            <p className="mt-2 text-sm text-stone-600" data-testid="pantry-barcode-result-meta">
              {selectedMatch.retailer_name} · {selectedMatch.pack_size} {selectedMatch.unit} · {selectedMatch.purchase_unit_label}
            </p>
            <p className="mt-2 text-xs text-stone-500" data-testid="pantry-result-defaults-meta">
              Standaard: {selectedMatch.default_location} · {selectedMatch.default_status}
            </p>
            {resolved.has_multiple_matches ? (
              <Button variant="outline" className="mt-3 rounded-full border-stone-200 bg-white" onClick={() => setSelectorOpen(true)} data-testid="pantry-barcode-open-variant-sheet-button">
                Kies variant
              </Button>
            ) : null}

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" htmlFor="inventory-quantity">Quantity</label>
                <Input id="inventory-quantity" type="number" min="0" value={confirmForm.quantity} onChange={(event) => setConfirmForm((current) => ({ ...current, quantity: event.target.value }))} className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="inventory-confirm-quantity-input" />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Unit</label>
                <Select value={confirmForm.unit} onValueChange={(value) => setConfirmForm((current) => ({ ...current, unit: value }))}>
                  <SelectTrigger className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="inventory-confirm-unit-select">
                    <SelectValue placeholder="Unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="g">g</SelectItem>
                    <SelectItem value="ml">ml</SelectItem>
                    <SelectItem value="pcs">pcs</SelectItem>
                    <SelectItem value="slice">slice</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-3 space-y-2">
              <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" htmlFor="inventory-purchase-label">Purchase unit label</label>
              <Input id="inventory-purchase-label" value={confirmForm.purchase_unit_label} onChange={(event) => setConfirmForm((current) => ({ ...current, purchase_unit_label: event.target.value }))} className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="inventory-confirm-purchase-label-input" />
            </div>

            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Location</label>
                <Select value={confirmForm.location} onValueChange={(value) => setConfirmForm((current) => ({ ...current, location: value }))}>
                  <SelectTrigger className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="inventory-confirm-location-select">
                    <SelectValue placeholder="Location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pantry">pantry</SelectItem>
                    <SelectItem value="fridge">fridge</SelectItem>
                    <SelectItem value="freezer">freezer</SelectItem>
                    <SelectItem value="stock">stock</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Status</label>
                <Select value={confirmForm.status} onValueChange={(value) => setConfirmForm((current) => ({ ...current, status: value }))}>
                  <SelectTrigger className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="inventory-confirm-status-select">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sealed">sealed</SelectItem>
                    <SelectItem value="opened">opened</SelectItem>
                    <SelectItem value="fresh">fresh</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-3 space-y-2">
              <label className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" htmlFor="inventory-expiry">Houdbaar tot</label>
              <Input id="inventory-expiry" type="date" value={confirmForm.expiry_date} onChange={(event) => setConfirmForm((current) => ({ ...current, expiry_date: event.target.value }))} className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="inventory-confirm-expiry-input" />
            </div>

            <Button className="mt-4 rounded-full bg-emerald-700 text-white hover:bg-emerald-800" onClick={confirmInventoryItem} disabled={busy} data-testid="inventory-confirm-button">
              <PackagePlus className="mr-2 h-4 w-4" />
              Bevestig toevoegen
            </Button>
          </div>
        ) : null}
      </section>

      {selectorOpen && resolved ? (
        <div className="fixed inset-0 z-[130] flex items-end bg-stone-950/30 px-4 pb-4 pt-12" data-testid="pantry-variant-sheet-overlay">
          <div className="w-full rounded-[28px] border border-stone-200 bg-white p-4 shadow-2xl" data-testid="pantry-variant-sheet">
            <div className="mx-auto h-1.5 w-14 rounded-full bg-stone-200" />
            <p className="mt-4 text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" data-testid="pantry-variant-sheet-kicker">Meerdere matches</p>
            <h3 className="mt-2 text-lg font-semibold text-stone-900" data-testid="pantry-variant-sheet-title">Kies de juiste productvariant.</h3>
            <div className="mt-4 space-y-2">
              {resolved.matches.map((match) => (
                <button key={match.variant_id} type="button" className="w-full rounded-[22px] border border-stone-200 bg-stone-50 px-4 py-3 text-left transition-colors hover:bg-stone-100" onClick={() => { setSelectedVariantId(match.variant_id); setSelectorOpen(false); }} data-testid={`pantry-variant-option-${match.variant_id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-stone-900">{match.ingredient_name}</p>
                    <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-stone-600">{match.category}</span>
                  </div>
                  <p className="mt-2 text-sm text-stone-600">{match.retailer_name} · {match.pack_size} {match.unit}</p>
                  <p className="mt-1 text-xs text-stone-500">{match.purchase_unit_label}</p>
                  <p className="mt-1 text-xs text-emerald-700">{match.confidence_label}</p>
                </button>
              ))}
            </div>
            <Button variant="outline" className="mt-4 w-full rounded-full border-stone-200 bg-white" onClick={() => setSelectorOpen(false)} data-testid="pantry-variant-sheet-close-button">
              Sluiten
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
};